
import {
  Vue, Component,
} from 'vue-property-decorator';
import EditLeadPaymentsDialog from '@/components/EditLeadPaymentsDialog.vue';
import PermissionsInfo from '@/components/PermissionsInfo.vue';
import { PermissionCode } from '@/types';
import UTILS from '@/lib/UTILS';
import { formatPaymentsDetails, escapeHtml, formatDate } from '@/helpers';

@Component({
  components: {
    EditLeadPaymentsDialog,
    PermissionsInfo,
  },
})
export default class LeadPayments extends Vue {
  editDialog = false

  get user() {
    return this.$store.getters.currentUser;
  }

  get userPermissions() {
    return this.$store.getters.currentUserPermissions;
  }

  get lead() {
    return this.$store.getters.currentLead;
  }

  get invoiceDiscount() {
    return this.getCustomValueTextByName('invoiceDiscount');
  }

  get loan() {
    return this.getCustomValueTextByName('loan');
  }

  get invoiceDiscountIconTooltipText() {
    if (this.invoiceDiscount === 'yes') return 'Sconto in fattura: sì';
    if (this.invoiceDiscount === 'no') return 'Sconto in fattura: no';
    return 'Sconto in fattura: non definito';
  }

  get loanIconTooltipText() {
    if (this.loan === 'yes') return 'Finanziamento: sì';
    if (this.loan === 'no') return 'Finanziamento: no';
    return 'Finanziamento: non definito';
  }

  get invoiceDiscountIconColor(): string {
    if (this.invoiceDiscount === 'yes') return 'success';
    if (this.invoiceDiscount === 'no') return 'grey lighten-2';
    return 'grey lighten-2';
  }

  get loanIconColor(): string {
    if (this.loan === 'yes') return 'success';
    if (this.loan === 'no') return 'grey lighten-2';
    return 'grey lighten-2';
  }

  get invoiceDiscountTooltipColor(): string {
    if (this.invoiceDiscount === 'yes') return 'success';
    if (this.invoiceDiscount === 'no') return '';
    return '';
  }

  get loanTooltipColor(): string {
    if (this.loan === 'yes') return 'success';
    if (this.loan === 'no') return '';
    return '';
  }

  get invoiceDiscountIcon(): string {
    if (this.invoiceDiscount === 'yes') return 'mdi-sale';
    if (this.invoiceDiscount === 'no') return 'mdi-sale';
    return 'mdi-sale-outline';
  }

  get loanIcon(): string {
    if (this.loan === 'yes') return 'mdi-hand-coin';
    if (this.loan === 'no') return 'mdi-hand-extended';
    return 'mdi-hand-extended-outline';
  }

  get isEditable() {
    if (!this.lead) return false;

    return this.lead
      && 'status' in this.lead
      && (this.lead.status === 'open' || this.lead.status === 'new')
      && this.hasPermission('payments.edit');
  }

  get payments() {
    return this.lead.payments;
  }

  get color() {
    if (this.payments === -1) return 'grey';
    if (this.payments === 0) return 'error';
    if (this.payments === 1) return 'orange';
    if (this.payments === 2) return 'lime';
    if (this.payments === 3) return 'light-green';
    if (this.payments === 4) return 'success';
    return '';
  }

  get description() {
    if (this.payments === -1) return 'Nessun pagamento dovuto';
    if (this.payments === 0) return 'Nessun pagamento effettuato';
    if (this.payments === 1) return 'Primo pagamento effettuato';
    if (this.payments === 2) return 'Secondo pagamento effettuato';
    if (this.payments === 3) return 'Pagamento installazione parziale effettuato';
    if (this.payments === 4) return 'Pagamento finale effettuato';
    return null;
  }

  get latestPaymentDate(): string | null {
    const date = this.getCustomValueDateByName('latestPaymentDate');
    return formatDate(date);
  }

  get paymentsDetails(): string {
    const paymentsData = {
      first: {
        paymentDate: this.getCustomValueDateByName('firstPaymentDate'),
        invoiceRef: this.getCustomValueTextByName('firstPaymentInvoiceRef'),
        invoiceDate: this.getCustomValueDateByName('firstPaymentInvoiceDate'),
        label: 'Primo pagamento',
      },
      second: {
        paymentDate: this.getCustomValueDateByName('secondPaymentDate'),
        invoiceRef: this.getCustomValueTextByName('secondPaymentInvoiceRef'),
        invoiceDate: this.getCustomValueDateByName('secondPaymentInvoiceDate'),
        label: 'Secondo pagamento',
      },
      partialInstallation: {
        paymentDate: this.getCustomValueDateByName('partialInstallationPaymentDate'),
        invoiceRef: this.getCustomValueTextByName('partialInstallationPaymentInvoiceRef'),
        invoiceDate: this.getCustomValueDateByName('partialInstallationPaymentInvoiceDate'),
        label: 'Pagamento install. parz.',
      },
      final: {
        paymentDate: this.getCustomValueDateByName('finalPaymentDate'),
        invoiceRef: this.getCustomValueTextByName('finalPaymentInvoiceRef'),
        invoiceDate: this.getCustomValueDateByName('finalPaymentInvoiceDate'),
        label: 'Pagamento finale',
      },
    };

    // Le note vengono formattate dalla funzione helper
    const paymentsNotes = this.getCustomValueTextByName('paymentsNotes');

    return formatPaymentsDetails(paymentsData, paymentsNotes);
  }

  get notes() {
    const text = this.getCustomValueTextByName('paymentsNotes');
    return text ? escapeHtml(text).replace(/\n/g, '<br>') : null;
  }

  hasPermission(codename: PermissionCode) {
    if (!this.user) return false;
    if (this.user.isSuperUser) return true;

    return this.userPermissions.includes(codename);
  }

  getCustomValueTextByName(name: string): string | null {
    return UTILS.getCustomValueTextByName(this.lead, name) || null;
  }

  getCustomValueDateByName(name: string): Date | null {
    return UTILS.getCustomValueDateByName(this.lead, name) || null;
  }

  get financingIntegration() {
    return this.$store.getters.getIntegrations.financingPortal;
  }

  // Portale esterno spento: il permesso da solo non basta, i Super Utenti lo
  // scavalcano. L'indirizzo arriva dal backend, non e' piu' cablato.
  get showFinancingPortal(): boolean {
    return this.financingIntegration.enabled && this.hasPermission('financing.portal.access');
  }

  openFinancingPortal() {
    if (!this.financingIntegration.url) return;
    window.open(this.financingIntegration.url, '_blank');
  }

  onEdit() {
    this.editDialog = true;
  }
}
